import React from 'react'
import { Input, InputNumber, theme } from 'antd'
import type { Variant as AntVariant } from 'antd/es/config-provider'
import { Field } from '../Field/Field'
import type { FieldControlBag, FieldLabelling, FieldOwnProps, FieldSize } from '../Field/types'

export type TextFieldVariant = 'text' | 'email' | 'number' | 'textarea'
/** The library's one size vocabulary, shared with every other field. */
export type TextFieldSize = FieldSize
export type TextFieldInputStyle = 'outlined' | 'borderless'
/**
 * Validation state. antd itself calls this `status` with values `'error' | 'warning'` -
 * renamed `'error'` to `'danger'` here for consistency with the rest of this library.
 */
export type TextFieldStatus = 'danger' | 'warning'

type TextFieldValue = string | number

interface TextFieldOwnProps extends FieldOwnProps {
  /** Which antd primitive to render underneath. */
  variant?: TextFieldVariant
  size?: TextFieldSize
  /** antd's own `variant`, renamed so it doesn't clash with this component's `variant`. */
  inputStyle?: TextFieldInputStyle
  clearable?: boolean
  /** 'number' variant only - rendered as antd's `addonAfter`, e.g. "days". Replaces the ad hoc `addonAfter="days"` repeated across the automation forms' quota fields. */
  unit?: string
  /** 'textarea' variant only */
  rows?: number
  /** 'textarea' variant only - antd `Input.TextArea`'s own `autoSize` (grows with content). */
  autoSize?: boolean | { minRows?: number; maxRows?: number }
  /** 'textarea' variant only */
  showCount?: boolean
  /** 'text'/'email'/'textarea' variants only - antd's own `maxLength`, e.g. the 100-char team-name cap. */
  maxLength?: number
  /** 'number' variant only - antd `InputNumber`'s own `min`. */
  min?: number
  /** 'number' variant only - antd `InputNumber`'s own `max`. */
  max?: number
  /** 'number' variant only - antd `InputNumber`'s own `step`. */
  step?: number
  /** Validation state. antd itself calls this `status` with values `'error' | 'warning'` - renamed `'error'` to `'danger'` here for consistency with the rest of this library. */
  status?: TextFieldStatus
  disabled?: boolean
  placeholder?: string
  value?: TextFieldValue
  defaultValue?: TextFieldValue
  onChange?: (value: TextFieldValue | null) => void
  autoFocus?: boolean
  /** Forwarded as-is - several callers submit on Enter. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** Forwarded as-is to the underlying antd control - several callers validate on blur rather than on every keystroke. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

/** `label` or `ariaLabel`, never neither - a text box without a name fails axe. */
export type TextFieldProps = TextFieldOwnProps & FieldLabelling

const SIZE_TO_ANT: Record<TextFieldSize, 'small' | 'middle' | 'large'> = {
  small: 'small',
  default: 'middle',
  large: 'large',
}

const INPUT_STYLE_TO_ANT: Record<TextFieldInputStyle, AntVariant> = {
  outlined: 'outlined',
  borderless: 'borderless',
}

/**
 * The one text/number input, rendering antd `Input`, `InputNumber` or `Input.TextArea` by
 * `variant`. Its chrome comes from `Field`.
 *
 * Every chrome prop is destructured rather than left to a rest spread: `required` reaching a real
 * `<input>` turns on the browser's validation bubble, which then fights antd's.
 */
export function TextField({
  variant = 'text',
  size = 'default',
  inputStyle = 'outlined',
  clearable = false,
  unit,
  rows,
  autoSize,
  maxLength,
  min,
  max,
  step,
  placeholder,
  value,
  defaultValue,
  onChange,
  autoFocus,
  onKeyDown,
  onBlur,
  // On the control, not the wrapper: callers size the input with these and some key CSS off it.
  style,
  className,
  ...field
}: TextFieldProps) {
  const { token } = theme.useToken()
  const antSize = SIZE_TO_ANT[size]
  const antVariant = INPUT_STYLE_TO_ANT[inputStyle]
  const antStatus: 'error' | 'warning' | undefined = field.error !== undefined ? 'error' : field.warning !== undefined ? 'warning' : undefined

  const shared = {
    size: antSize,
    variant: antVariant,
    status: antStatus,
    placeholder,
    autoFocus,
    onKeyDown,
    onBlur,
    style,
    className,
  }

  return (
    <Field {...field}>
      {(control) => {
        // `checked` only arrives for a checkbox-shaped field.
        const { value: boundValue, checked: _checked, onChange: commit, ...wiring } = control as FieldControlBag
        const isBound = 'onChange' in control
        const currentValue = isBound ? (boundValue as TextFieldValue | undefined) : value

        if (variant === 'number') {
          return (
            <InputNumber
              {...shared}
              {...wiring}
              value={currentValue as number | undefined}
              defaultValue={defaultValue as number | undefined}
              // rc-field-form takes either shape: an event for the text inputs, a bare value here.
              onChange={(next) => {
                commit?.(next)
                onChange?.(next as number | null)
              }}
              min={min}
              max={max}
              step={step}
              {...(unit !== undefined ? { suffix: <span style={{ color: token.colorTextDescription }}>{unit}</span> } : {})}
            />
          )
        }

        if (variant === 'textarea') {
          return (
            <Input.TextArea
              {...shared}
              {...wiring}
              value={currentValue as string | undefined}
              defaultValue={defaultValue as string | undefined}
              onChange={(event) => {
                commit?.(event)
                onChange?.(event.target.value)
              }}
              rows={rows}
              autoSize={autoSize}
              maxLength={maxLength}
              allowClear={clearable}
            />
          )
        }

        return (
          <Input
            {...shared}
            {...wiring}
            type={variant === 'email' ? 'email' : 'text'}
            value={currentValue as string | undefined}
            defaultValue={defaultValue as string | undefined}
            onChange={(event) => {
              commit?.(event)
              onChange?.(event.target.value)
            }}
            maxLength={maxLength}
            allowClear={clearable}
          />
        )
      }}
    </Field>
  )
}

export default TextField
